import { useMemo } from 'react';
import type { Comment } from '../containers/CommentList/CommentList.types';
import { DEFAULT_INITIAL_VISIBLE } from '../components/LoadMoreList/LoadMoreList';

export interface UseGetCommentInitialPageStartOptions {
  /** Comment id to find (e.g. from URL or state). */
  commentId: string | null | undefined;
  /** Same comments array passed to CommentList (order must match). */
  comments: Comment[];
  /** Page size used by Load more (must match initialVisibleCount). Default: LoadMoreList.DEFAULT_INITIAL_VISIBLE. */
  pageSize?: number;
}

export interface GetCommentInitialPageStartResult {
  /** Whether the comment was found in the list. */
  found: boolean;
  /** Start index for the visible window so this comment is in view. Pass as initialStartIndex to CommentList LOAD_MORE. */
  pageStart: number;
  /** The comment id (for consumer's scrollIntoView). */
  commentId: string | null;
}

/**
 * Hook: getCommentInitialPageStart (useGetCommentInitialPageStart)
 *
 * For use in the consuming application with Load more. Pass commentId and comments;
 * returns found, pageStart (and commentId) so you can pass pageStart as
 * initialStartIndex to CommentList and handle scrollIntoView in your app.
 *
 * Uses the same display order as CommentList: unresolved (OPEN) first, then resolved.
 *
 * For scroll-to-comment to work, pageSize must equal the list's initialVisibleCount
 * (so the initial window rendered by the list includes the target comment). Omit both
 * to use LoadMoreList.DEFAULT_INITIAL_VISIBLE (10), or pass the same value to both.
 */
export function useGetCommentInitialPageStart({
  commentId,
  comments,
  pageSize = DEFAULT_INITIAL_VISIBLE
}: UseGetCommentInitialPageStartOptions): GetCommentInitialPageStartResult {
  return useMemo(() => {
    const id = commentId ?? null;
    if (!id || !comments?.length) {
      return {
        found: false,
        pageStart: 0,
        commentId: id
      };
    }

    const combined = [
      ...comments.filter(c => c.commentStatus !== 'RESOLVED'),
      ...comments.filter(c => c.commentStatus === 'RESOLVED')
    ];
    const index = combined.findIndex(c => c.id === id);
    const found = index >= 0;
    const size = Math.max(1, pageSize);
    const maxStart = Math.max(0, combined.length - size);
    const pageStart = found ? Math.min(index, maxStart) : 0;

    return {
      found,
      pageStart,
      commentId: id
    };
  }, [commentId, comments, pageSize]);
}
